import each from 'lodash/each';
import moment from 'moment';
import type {Moment} from 'moment';

import {UnformattedUserEvent} from 'common/report/util/profile/types';

const ARB_MAX_EVENTS_LIMIT = 6000;

export interface ActivityStreamFieldsToUpdate {
  events: UnformattedUserEvent[];
  enableShowMoreButton: boolean;
  earliestTime: number;
  isAtHistoryLimit: boolean;
  numEventsToRequest: number;

  // Not a state variable, but triggers a re-query of data
  shouldRerequest: boolean;
}

export function partitionEventsOutOfRange(
  low: number,
  high: number,
  events: UnformattedUserEvent[],
): {
  lowerEvents: UnformattedUserEvent[];
  higherEvents: UnformattedUserEvent[];
} {
  const lowerEvents = [];
  const higherEvents = [];

  each(events, (event) => {
    if (event.properties[`time`] < low) {
      lowerEvents.push(event);
    }
    if (event.properties[`time`] > high) {
      higherEvents.push(event);
    }
    // Otherwise we drop the event, since it is cached
  });

  return {
    lowerEvents,
    higherEvents,
  };
}

export function noEarlierEvents({
  cachedEventsLength,
  laterEventsLength,
  numEventsToRequest,
  oldestTimeIsAtLimit,
}: {
  cachedEventsLength: number;
  laterEventsLength: number;
  numEventsToRequest: number;
  oldestTimeIsAtLimit: boolean;
}): {shouldRerequest: boolean; numEventsToRequest: number; enableShowMoreButton: boolean; isAtHistoryLimit: boolean} {
  let shouldRerequest = false;
  let isAtHistoryLimit = false;
  let enableShowMoreButton = true;

  /* There is a rare edge case in which no new earlier events can appear.
   * This can happen when the number of events we request (`numEventsToRequest` events) all happen on the most recent (toDate) day.
   * A subsequent request gets the same events. In this case, we have to increase the number of events to request
   * (doubling should get us there safely and shouldn't have much performance impact, unless the number of events for a single
   *  user is more than query-api can handle; if that happens the endpoint will have to be redesigned with timestamp granularity),
   * to get out of this state.
   * This ceases happening as soon as we can move the toDate backwards, as then we start getting events from the previous day.
   */
  if (cachedEventsLength + laterEventsLength >= numEventsToRequest) {
    numEventsToRequest = numEventsToRequest * 2;

    // Only query if we're below the arb limit
    if (numEventsToRequest <= ARB_MAX_EVENTS_LIMIT) {
      shouldRerequest = true;
    } else {
      enableShowMoreButton = false;
    }
  } else {
    /* There is another case in which this can happen:
     * If a user has less than NUM_EVENTS_LIMIT events total for the time period requested, or a history time limit has
     * limited the request, then we can get no new (i.e. unseen by us) older events, but in this case we don't want to re-request data.
     */
    isAtHistoryLimit = oldestTimeIsAtLimit;
    enableShowMoreButton = false;
  }

  return {
    shouldRerequest,
    numEventsToRequest,
    enableShowMoreButton,
    isAtHistoryLimit,
  };
}

export function getActivityStreamFieldsToUpdate(
  cachedEvents: UnformattedUserEvent[],
  newEvents: UnformattedUserEvent[],
  {
    numEventsToRequest,
    numberOfEventsDisplayed,
    fromDate,
    historyLimit,
  }: {
    numEventsToRequest: number;
    numberOfEventsDisplayed: number;
    fromDate: Moment;
    historyLimit: Moment;
  },
): ActivityStreamFieldsToUpdate {
  let allEvents = [];
  let enableShowMoreButton = true;
  let shouldRerequest = false;
  let isAtHistoryLimit = false;

  // find where the events from the server overlap the events we have cached.
  if (cachedEvents.length > 0) {
    // We can get both events that are before our cached events and after our cached events,
    // so we put them into two separate arrays that we will concat at the end
    const newestPreviouslySeenTime = cachedEvents[cachedEvents.length - 1].properties[`time`];
    const oldestPreviouslySeenTime = cachedEvents[0].properties[`time`];
    const {lowerEvents: earlierEvents, higherEvents: laterEvents} = partitionEventsOutOfRange(
      oldestPreviouslySeenTime,
      newestPreviouslySeenTime,
      newEvents,
    );

    if (earlierEvents.length === 0) {
      const earlierEventsResult = noEarlierEvents({
        cachedEventsLength: cachedEvents.length,
        laterEventsLength: laterEvents.length,
        numEventsToRequest,
        oldestTimeIsAtLimit: moment(newestPreviouslySeenTime * 1000).isSame(historyLimit, `day`),
      });

      shouldRerequest = earlierEventsResult.shouldRerequest;
      numEventsToRequest = earlierEventsResult.numEventsToRequest;
      enableShowMoreButton = earlierEventsResult.enableShowMoreButton;
      isAtHistoryLimit = earlierEventsResult.isAtHistoryLimit;
    }

    // If we've got more recent events, don't count these against the limit of extra events to show
    if (laterEvents.length > 0) {
      numberOfEventsDisplayed += laterEvents.length;
    }

    allEvents = earlierEvents.concat(cachedEvents).concat(laterEvents);
  } else {
    // No cached events (initial request case)
    allEvents = newEvents;
  }

  // if we're at the history limit, don't allow the option to show more
  if (fromDate.isBefore(historyLimit)) {
    enableShowMoreButton = false;
    isAtHistoryLimit = true;
  }

  // if we have less than requested number of events and we're showing all the events, future queries won't get anything so don't show more
  if (allEvents.length < numEventsToRequest && numberOfEventsDisplayed >= allEvents.length) {
    enableShowMoreButton = false;
  }

  return {
    enableShowMoreButton,
    isAtHistoryLimit,
    numEventsToRequest,
    shouldRerequest,
    earliestTime: allEvents[0]?.properties[`time`],
    events: allEvents,
  };
}
